import classNames from "classnames";
import get from "lodash/get";
import { useActivityTemplateType } from "PFApp/activities/hooks";
import { BookingCategorySelect as BookingCategorySelectInput } from "PFApp/booking/components/booking_category_select/booking_category_select";
import { useAvailableBookingCategories } from "PFApp/booking/components/booking_category_select/use_available_booking_categories";
import { useActivityWithParent } from "PFApp/booking/hooks/use_activity_with_parent";
import { TemplateKey } from "PFApp/constants/templates";
import { checkIsBookingCategoryLinked } from "PFApp/helpers/check_is_booking_category_linked";
import { useTemplate } from "PFApp/hooks";
import { getAttribute } from "PFCore/helpers/get_attribute";
import useBookingCategories from "PFCore/hooks/use_booking_categories";
import { AccessLevel, BookingCategory } from "PFTypes";
import { Option } from "PFTypes/options";
import { Controller, FieldPath, useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { BookingDetailCategorySettings } from "../../booking_detail_category_settings";
import { truncateTitle } from "../booking_form_common_inputs.utils";
import { BookingFormValues } from "../booking_form_values.types";
import { useCategoryErrorMessage } from "../use_category_error";
import css from "./booking_category_select.module.scss";

interface BookingCategorySelectProps {
  categoryFormValueName: FieldPath<BookingFormValues>;
  categoryFormErrorKeys: string[];
  titleFormValueName: FieldPath<BookingFormValues>;
  getTip?: (data: { options: Option<BookingCategory>[] }) => string | undefined;
}

export const BookingCategorySelect = ({
  categoryFormValueName,
  categoryFormErrorKeys,
  titleFormValueName,
  getTip
}: BookingCategorySelectProps) => {
  const { t } = useTranslation("bookingModule");
  const {
    control,
    setValue,
    clearErrors,
    formState: { errors }
  } = useFormContext<BookingFormValues>();
  const [activityId, title] = useWatch<BookingFormValues, ["activityId", any]>({
    name: ["activityId", titleFormValueName]
  });
  const category = useWatch<BookingFormValues>({ name: categoryFormValueName }) as any;
  const { getCategoryErrorMessage } = useCategoryErrorMessage({ categoryFormValueName });
  const {
    activity: bookingActivity,
    parentActivity: parentBookingActivity,
    isActivityFetching,
    isParentActivityFetching
  } = useActivityWithParent({ activityId });

  const { isAudit, isRole } = useActivityTemplateType(bookingActivity);
  const engagementTemplate = useTemplate(isAudit ? TemplateKey.AuditEngagement : TemplateKey.Engagement);
  const roleTemplate = useTemplate(isAudit ? TemplateKey.AuditRole : TemplateKey.Role);

  const { getBookingCategory } = useBookingCategories();
  const getAvailableBookingCategories = useAvailableBookingCategories();
  const availableBookingCategoryValues = getAvailableBookingCategories({
    activityData: bookingActivity
      ? [
          {
            activityTemplateId: bookingActivity.template_id,
            activitySubtemplateKey: bookingActivity.subtemplate_key,
            parentActivitySubtemplateKey: parentBookingActivity?.subtemplate_key
          }
        ]
      : []
  });

  const { isLinked, canLink } = checkIsBookingCategoryLinked({
    activity: bookingActivity,
    activityTemplate: isRole ? roleTemplate : engagementTemplate,
    bookingCategoryId: category?.id,
    availableBookingCategoryValues
  });
  const parentCategory = getBookingCategory(bookingActivity?.booking_category_id);
  const canRestore =
    !isLinked && canLink && getAttribute(parentCategory, "bookingsPermissions") === AccessLevel.ReadWrite;

  const handleChange = (newCategory: BookingCategory | null, onChange: (value?: BookingCategory) => void) => {
    clearErrors(categoryFormErrorKeys as (keyof BookingFormValues)[]);
    const isTitlePreviousCategoryName = truncateTitle(category?.display_as) === title;
    if (newCategory && (!title || isTitlePreviousCategoryName)) {
      setValue(titleFormValueName, truncateTitle(newCategory.display_as));
    }
    onChange(newCategory ?? undefined);
  };

  const handleRestore = () => {
    if (canLink && parentCategory) {
      setValue(categoryFormValueName, parentCategory, { shouldValidate: true });
    }
  };

  const errorKey = categoryFormErrorKeys.find((key) => !!get(errors, key));
  const error = errorKey ? get(errors, errorKey) : undefined;

  const getTipOrWarning = () => {
    if (getTip) {
      return { getTip };
    }
    if (!canLink) {
      return undefined;
    }
    if (isLinked) {
      return { getTip: () => t("bookings.create.categoryLinkedToActivity") };
    } else {
      return { warning: t("bookings.create.categoryNotLinkedToActivity") };
    }
  };

  return (
    <div className={css.root}>
      <Controller
        name={categoryFormValueName}
        control={control}
        rules={{
          required: true,
          validate: (category: BookingCategory | null | undefined, formValues) => {
            const isDemandRelated = !!formValues["activityId"];
            const errorMessage = getCategoryErrorMessage({ category, isDemandRelated });
            return errorMessage || true;
          }
        }}
        render={({ field }) => (
          <BookingCategorySelectInput
            label={t("bookings.create.category")}
            category={field.value as BookingCategory}
            onCategoryChange={(newCategory) => handleChange(newCategory, field.onChange)}
            error={error?.message as string}
            required
            demand={!!activityId}
            disabled={isActivityFetching || isParentActivityFetching}
            filterBookingCategory={({ id, bookings_permissions: bookingPermissions }) =>
              bookingPermissions === AccessLevel.ReadWrite && availableBookingCategoryValues.includes(id)
            }
            onRestore={canRestore ? handleRestore : undefined}
            {...getTipOrWarning()}
          />
        )}
      />
      <div
        className={classNames({
          [css.boxDisabled]: !category
        })}
      >
        <BookingDetailCategorySettings
          billable={category?.billable}
          affectsAvailability={category?.affects_availability}
        />
      </div>
    </div>
  );
};
